import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getCalendarExceptions, createCalendarException, deleteCalendarException } from '../lib/api';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { es } from 'date-fns/locale';
import {
  datePickerToClinicYmd,
  formatAppointmentDateOnlyInClinic,
  ymdToDatePickerDate,
} from '../lib/clinicTime';

export default function AdminSchedule() {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newException, setNewException] = useState({
    date: null,
    reason: '',
    allDay: true,
    closedStart: '14:00',
    closedEnd: '17:00',
  });

  const formatExceptionScope = (exc) => {
    const isAllDay = exc.all_day !== false && !String(exc.closed_start || '').trim();
    if (isAllDay) return 'Todo el día';
    const a = String(exc.closed_start || '').slice(0, 5);
    const b = String(exc.closed_end || '').slice(0, 5);
    if (a && b) return `${a} – ${b}`;
    return 'Horario parcial';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const exceptionsData = await getCalendarExceptions();
      setExceptions(exceptionsData);
    } catch (error) {
      toast.error('Error al cargar excepciones');
    } finally {
      setLoading(false);
    }
  };

  const handleAddException = async () => {
    if (!newException.date || !newException.reason.trim()) {
      toast.error('Completa fecha y motivo');
      return;
    }
    if (!newException.allDay) {
      if (!newException.closedStart || !newException.closedEnd) {
        toast.error('Indica hora de inicio y fin del cierre');
        return;
      }
      if (newException.closedStart >= newException.closedEnd) {
        toast.error('La hora de fin debe ser posterior a la de inicio');
        return;
      }
    }
    try {
      const payload = {
        date: datePickerToClinicYmd(newException.date),
        reason: newException.reason.trim(),
        is_closed: true,
        all_day: newException.allDay,
      };
      if (!newException.allDay) {
        payload.closed_start = newException.closedStart;
        payload.closed_end = newException.closedEnd;
      }
      await createCalendarException(payload);
      toast.success('Excepción agregada');
      setDialogOpen(false);
      setNewException({
        date: null,
        reason: '',
        allDay: true,
        closedStart: '14:00',
        closedEnd: '17:00',
      });
      loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al agregar excepción');
    }
  };

  const handleDeleteException = async (id) => {
    if (!window.confirm('¿Eliminar esta excepción?')) return;
    try {
      await deleteCalendarException(id);
      toast.success('Excepción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar excepción');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando excepciones...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-schedule">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Cierres de calendario</h1>
        <p className="text-muted-foreground">
          Días o tramos sin consulta que aplican a <strong>todos los servicios</strong>. El horario
          de atención de cada servicio se configura en <strong>Admin → Tipos de cita</strong>.
        </p>
      </div>

      <Card className="border border-border max-w-3xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            Excepciones del calendario
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-full gap-1" data-testid="add-exception-btn">
                <Plus className="w-4 h-4" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Agregar excepción de calendario</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Fecha</Label>
                  <Calendar
                    mode="single"
                    selected={newException.date}
                    onSelect={(date) => {
                      if (!date) {
                        setNewException((prev) => ({ ...prev, date: null }));
                        return;
                      }
                      const ymd = datePickerToClinicYmd(date);
                      setNewException((prev) => ({
                        ...prev,
                        date: ymdToDatePickerDate(ymd),
                      }));
                    }}
                    fromDate={new Date()}
                    locale={es}
                    className="rounded-lg border mt-2"
                    data-testid="exception-calendar"
                  />
                </div>
                <div>
                  <Label htmlFor="exception_reason">Motivo</Label>
                  <Input
                    id="exception_reason"
                    value={newException.reason}
                    onChange={(e) => setNewException((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Ej: Día festivo, curso, mantenimiento"
                    className="h-11 mt-1"
                    data-testid="exception-reason-input"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-3">
                  <Checkbox
                    checked={newException.allDay}
                    onCheckedChange={(c) =>
                      setNewException((prev) => ({ ...prev, allDay: Boolean(c) }))
                    }
                    data-testid="exception-all-day"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Cerrar todo el día</span>
                    <span className="block text-muted-foreground text-xs mt-0.5">
                      Sin consultas en ningún horario de esa fecha
                    </span>
                  </span>
                </label>
                {!newException.allDay ? (
                  <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed p-3">
                    <div>
                      <Label htmlFor="exception_closed_start">Desde (sin servicio)</Label>
                      <Input
                        id="exception_closed_start"
                        type="time"
                        className="h-11 mt-1"
                        value={newException.closedStart}
                        onChange={(e) =>
                          setNewException((prev) => ({ ...prev, closedStart: e.target.value }))
                        }
                        data-testid="exception-closed-start"
                      />
                    </div>
                    <div>
                      <Label htmlFor="exception_closed_end">Hasta (sin servicio)</Label>
                      <Input
                        id="exception_closed_end"
                        type="time"
                        className="h-11 mt-1"
                        value={newException.closedEnd}
                        onChange={(e) =>
                          setNewException((prev) => ({ ...prev, closedEnd: e.target.value }))
                        }
                        data-testid="exception-closed-end"
                      />
                    </div>
                    <p className="col-span-2 text-xs text-muted-foreground">
                      Esas horas no aparecerán como disponibles en la web ni en WhatsApp.
                    </p>
                  </div>
                ) : null}
                <Button onClick={handleAddException} className="w-full rounded-full" data-testid="save-exception-btn">
                  Agregar excepción
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {exceptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay excepciones configuradas</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Alcance</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((exception) => (
                  <TableRow key={exception.id} data-testid={`exception-row-${exception.id}`}>
                    <TableCell>
                      {formatAppointmentDateOnlyInClinic(exception.date, 'd MMM yyyy')}
                    </TableCell>
                    <TableCell className="text-sm">{formatExceptionScope(exception)}</TableCell>
                    <TableCell className="text-muted-foreground">{exception.reason}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-accent hover:text-accent"
                        onClick={() => handleDeleteException(exception.id)}
                        data-testid={`delete-exception-${exception.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
